import type { DeliveryAddressModel, DeliveryAddressModelV4, DeliveryAddressReqModel, DeliveryTypeReqModelV4 } from '@develop/ia-api-library'
import {
  setDeliveryAddressV4 as setDeliveryAddressAPI,
  setDeliveryTypeV4 as setDeliveryTypeAPI,
} from '@develop/ia-api-library'
import { type CheckoutDeliveryModelV2, type DeliveryFormOption, DeliveryOption } from '~/models/checkout-delivery-model-v2'
import { IaCookie } from '~/models/cookie'
import type { CheckoutDeliveryOptionModel } from '~/models/delivery'

const DEFAULT_DELIVERY: CheckoutDeliveryModelV2 = {
  address: null,
  deliveryType: null,
  deliveryPeriod: undefined,
}

export const useDelivery = () => {
  const authStore = useAuthStore()
  const checkoutStore = useCheckoutStore()
  const paymentStore = usePaymentStore()
  const currentPharmacyStore = useCurrentPharmacyStore()

  const { locale } = useTranslation()
  const anonymousUser = useAnonymousUser()
  const googleAnalyticsTracking = isClient ? useGoogleAnalytics() : undefined

  const isLoading = useState('delivery:loading', () => false)

  const anonymousUserId = computed(() => authStore.token ? undefined : anonymousUser.getToken())

  const deliveryOptions = computed(() => checkoutStore.checkoutStatus?.delivery.options)

  // const deliveryData = useLocalStorage<CheckoutDeliveryModelV2>(LocalStorage.DeliveryStorage, DEFAULT_DELIVERY, { serializer: StorageSerializers.object })
  // TODO: when useLocalStorage on reload value is again set to default, so we use useCookie instead
  const deliveryData = useCookie<CheckoutDeliveryModelV2>(IaCookie.DeliveryStorage, { default: () => DEFAULT_DELIVERY })

  const setDeliveryAddress = (address: DeliveryAddressModel | DeliveryAddressReqModel) => {
    set(deliveryData, { ...deliveryData.value, address })

    const { paymentData, convertDeliveryAddressToPaymentAddress, setAddress } = paymentStore
    if (paymentData.isIdenticalToDeliveryAddress) {
      const paymentAddress = convertDeliveryAddressToPaymentAddress(address)
      setAddress(paymentAddress)
    }
  }

  const setDeliveryType = (deliveryType: DeliveryFormOption) =>
    set(deliveryData, { ...deliveryData.value, deliveryType })

  const setDeliveryPeriod = (deliveryPeriod: {
    extended: string
    short: string
    id: number
  }) => set(deliveryData, { ...deliveryData.value, deliveryPeriod })

  // Info if pharmacy offers each type of delivery for selected address
  const isHomeDeliveryPossible = computed(() => {
    return (checkoutStore.checkoutStatus?.delivery.address?.allowedDeliveryTypes?.includes(DeliveryOption.HomeDelivery)
    ) ?? false
  })
  const isExpressDeliveryPossible = computed(() => {
    return (checkoutStore.checkoutStatus?.delivery.address?.allowedDeliveryTypes?.includes(DeliveryOption.ExpressDelivery)
    ) ?? false
  })

  const homeDeliveryLimit = computed(() => {
    const { airDistance, streetDistance } = currentPharmacyStore.deliverySettings?.standard?.deliveryLimits ?? {}
    return airDistance ? 'air-distance' : streetDistance ? 'street-distance' : 'zip-codes'
  })

  const expressDeliveryLimit = computed(() => {
    const { airDistance, streetDistance } = currentPharmacyStore.deliverySettings?.express?.deliveryLimits ?? {}
    return airDistance ? 'air-distance' : streetDistance ? 'street-distance' : 'zip-codes'
  })

  const isHomeDeliveryLimitByZipCodes = computed(() =>
    homeDeliveryLimit.value === 'zip-codes',
  )
  const isExpressDeliveryLimitByZipCodes = computed(() =>
    expressDeliveryLimit.value === 'zip-codes',
  )

  // Info if pharmacy offers each type of delivery in general
  const isDeliveryByAgreementAllowed = computed(() => deliveryOptions.value?.deliveryByAgreement?.allowed ?? false)
  const isHomeDeliveryAllowed = computed(() => deliveryOptions.value?.homeDelivery?.allowed ?? false)
  const isExpressDeliveryAllowed = computed(() => deliveryOptions.value?.expressDelivery?.allowed ?? false)

  const numberOfDeliveryOptions = computed(() => {
    if (!deliveryOptions.value)
      return 0

    let counter = 0

    for (const [key, option] of Object.entries(deliveryOptions.value)) {
      // Skip pickup option because it's always available
      if (key !== 'pickup' && (option as CheckoutDeliveryOptionModel)?.allowed)
        counter++
    }

    return counter
  })

  const hasOnlyOneOption = computed(() => {
    return numberOfDeliveryOptions.value === 1
  })

  const hasDeliveryOptions = computed(() => {
    return numberOfDeliveryOptions.value > 0
  })

  const confirmDeliveryType = async (type: DeliveryFormOption) => {
    if (!checkoutStore.draftId) {
      onShowLogs(() => console.log('Draft ID is missing.'))
      return false
    }

    if ((type === DeliveryOption.HomeDelivery && !isHomeDeliveryPossible.value)
      || (type === DeliveryOption.ExpressDelivery && !isExpressDeliveryPossible.value)) {
      onShowLogs(() => console.log('Selected type of delivery is not possible for selected address.'))
      return
    }

    const { token, refreshToken } = authStore
    const options = {
      token: token ?? '',
      refreshToken: refreshToken ?? '',
      language: locale,
    }

    const isHomeDelivery = type === DeliveryOption.HomeDelivery
    const pharmacyDeliveryTimes = checkoutStore.checkoutStatus?.delivery.options.homeDelivery?.deliveryTimes ?? []
    const selectedDeliveryPeriod = pharmacyDeliveryTimes[deliveryData.value.deliveryPeriod?.id ?? 0]
    const { dateTimeFrom, dateTimeTo } = selectedDeliveryPeriod ?? {}
    const period = isHomeDelivery
      ? {
          dateTimeFrom,
          dateTimeTo,
        }
      : undefined

    const payload: DeliveryTypeReqModelV4 = {
      anonymousUserId: anonymousUserId.value,
      type,
      period,
    }

    set(isLoading, true)
    const { data, success } = await setDeliveryTypeAPI(payload, checkoutStore.draftId, options)
    set(isLoading, false)

    if (success && data)
      checkoutStore.setCheckoutStatus(data)
  }

  // Receives either address ID or address object (for anonymous users)
  const confirmDeliveryAddress = async (address: number | DeliveryAddressModelV4) => {
    if (!checkoutStore.draftId) {
      onShowLogs(() => console.log('Draft ID is missing.'))
      return false
    }

    const { token, refreshToken } = authStore
    const options = {
      token: token ?? '',
      refreshToken: refreshToken ?? '',
      language: locale,
    }

    const payload = typeof address === 'number'
      ? {
          deliveryAddressId: address,
        }
      : {
          address,
          anonymousUserId: anonymousUserId.value!,
        }

    set(isLoading, true)
    const { data, success } = await setDeliveryAddressAPI(payload, checkoutStore.draftId, options)
    set(isLoading, false)

    if (success && data) {
      checkoutStore.setCheckoutStatus(data)
      googleAnalyticsTracking?.formSentEvent('EditAddressForm')
    }
  }

  const reset = () => set(deliveryData, DEFAULT_DELIVERY)

  return {
    deliveryData,
    setDeliveryAddress,
    setDeliveryType,
    setDeliveryPeriod,
    isHomeDeliveryPossible,
    isExpressDeliveryPossible,
    homeDeliveryLimit,
    expressDeliveryLimit,
    isHomeDeliveryLimitByZipCodes,
    isExpressDeliveryLimitByZipCodes,
    isDeliveryByAgreementAllowed,
    isHomeDeliveryAllowed,
    isExpressDeliveryAllowed,
    hasOnlyOneOption,
    confirmDeliveryType,
    confirmDeliveryAddress,
    deliveryOptions,
    hasDeliveryOptions,
    isLoading,
    reset,
  }
}
